import { Chip } from './UI';
import { getOTT } from '../lib/ottPlatforms';

export default function FilmCard({ film, avgRating, ratingCount, reviewCount, onClick }) {
  const hasPoster = film.poster_url && film.poster_url.startsWith('http');

  return (
    <article
      onClick={onClick}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        cursor: 'pointer',
        transition: 'border-color 0.25s, transform 0.25s, box-shadow 0.25s',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--gold-dim)';
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.12)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* POSTER */}
      <div style={{ width: '100%', height: 200, background: '#0a0a0f', background: 'var(--surface2)', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        {hasPoster ? (
          <img
            src={film.poster_url}
            alt={film.title}
            style={{ width: '100%', height: '100%', objectFit: 'contain', transition: 'transform 0.4s ease' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #0d0d14 0%, #181824 100%)',
            fontSize: 56,
          }}>
            {film.poster_emoji || '🎬'}
          </div>
        )}
        {/* Series badge */}
        {film.type === 'series' && (
          <div style={{
            position: 'absolute', bottom: 10, left: 10,
            background: 'rgba(59,130,246,0.85)', borderRadius: 4,
            padding: '2px 7px', fontFamily: "'DM Mono', monospace",
            fontSize: 9, letterSpacing: 1, color: '#fff',
            textTransform: 'uppercase', backdropFilter: 'blur(4px)',
          }}>📺 Series</div>
        )}
        {/* Language badge */}
        <div style={{
          position: 'absolute', top: 10, left: 10,
          background: 'rgba(4,4,6,0.82)', border: '1px solid rgba(212,175,55,0.3)',
          borderRadius: 4, padding: '2px 8px',
          fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1.5,
          color: 'var(--gold-dim)', textTransform: 'uppercase', backdropFilter: 'blur(4px)',
        }}>{film.language}</div>
        {/* Rating badge */}
        {avgRating && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(4,4,6,0.82)', border: '1px solid rgba(212,175,55,0.3)',
            borderRadius: 4, padding: '2px 8px', backdropFilter: 'blur(4px)',
          }}>
            <span style={{ color: 'var(--gold)', fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700 }}>
              ★ {avgRating.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* BODY */}
      <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '0 0 3px', lineHeight: 1.25 }}>{film.title}</h3>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 10 }}>{film.year}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
          {(film.genres || []).slice(0, 3).map(g => <Chip key={g}>{g}</Chip>)}
        </div>
        {/* OTT Platform badges */}
        {film.ott_platforms && film.ott_platforms.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {film.ott_platforms.slice(0, 4).map(id => {
              const p = getOTT(id);
              if (!p) return null;
              return (
                <div key={id} title={p.name} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: p.bgColor,
                  border: `1px solid ${p.color}55`,
                  borderRadius: 5, padding: '3px 7px',
                }}>
                  <span style={{ width: 13, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    dangerouslySetInnerHTML={{ __html: p.logo }} />
                  <span style={{ fontSize: 10, color: p.color, fontFamily: "'DM Mono', monospace", letterSpacing: 0.3 }}>
                    {p.name}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 'auto' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
              <span style={{ color: 'var(--gold)', fontSize: 20, fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                {avgRating ? avgRating.toFixed(1) : '—'}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>/10</span>
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{ratingCount} vote{ratingCount !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>IMDb <span style={{ color: 'var(--gold-dim)' }}>{film.imdb_rating}</span></div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{reviewCount} review{reviewCount !== 1 ? 's' : ''}</div>
          </div>
        </div>
      </div>
    </article>
  );
}
